/**
 * `scaleEntityRadii` — split out of `geometryTransforms.ts` (which sits at
 * the repo's 2000-line file-size ceiling) to make room for the GitHub Codex
 * review PR #558 round 6 P2 fix below. Mechanical move, zero behaviour
 * change to any pre-existing case — `geometryTransforms.ts` re-exports this
 * function so its own `from "./geometryTransforms"` import path is
 * unchanged for every existing caller/test.
 *
 * Scales the SIZE-like fields of an entity (radius / text height / pattern
 * scale / …) by `Math.abs(factor)` — companion to `transformEntityCoords`
 * (which scales POSITION-like point fields via the generic `fn` affine).
 * Every SCALE dispatch calls both.
 *
 * ONE ARM IS NOT A PLAIN MULTIPLY: MTEXT's CACHED MEASUREMENT (DXF groups
 * 42/43) is AutoCAD's own reading of the laid-out block, so it is scaled
 * only where the scaled block stays geometrically SIMILAR and CLEARED
 * otherwise (@codex PR #794 round 16 P2, issue #804 — that case's own
 * comment and `drawing-model::mtextUniformScalePreservesLayoutShape`).
 */

import {
  mtextUniformScalePreservesLayoutShape,
  type DrawingEntity,
} from "@dxf-ai-editor/drawing-model";

import { normalizeDeg360 } from "./mirrorAngleMath";
import {
  presencePreservingRotation,
  presencePreservingValue,
} from "./presencePreservingRotation";

export function scaleEntityRadii<T extends DrawingEntity>(
  entity: T,
  _base: { x: number; y: number },
  factor: number,
): T {
  const absFactor = Math.abs(factor);
  switch (entity.type) {
    // targeted-cad-mutation-testing EQUIVALENT MUTANT (GitHub Codex review
    // PR #558 round 6, verified survivor): ConditionalExpression removing
    // this case's `return` (falls through to the next `case "Arc":`
    // instead). Why equivalent: the "Circle" and "Arc" case BODIES are
    // byte-identical (`return { ...entity, radius: entity.radius *
    // absFactor }`) — a Circle entity's `.radius` field has the exact same
    // name/shape as an Arc's, so falling through into the Arc case's
    // (identical) statement produces the SAME returned object. Kept as two
    // explicit labels for readability/documentation (Circle and Arc are
    // conceptually different entities), not because the code paths differ.
    case "Circle":
      return { ...entity, radius: entity.radius * absFactor };
    case "Arc":
      return { ...entity, radius: entity.radius * absFactor };
    case "Text":
      return { ...entity, height: entity.height * absFactor };
    // GitHub Codex review PR #794 round 16, P2 — issue #804. A SCALE must
    // carry the CACHED LAYOUT MEASUREMENT (DXF groups 42/43) with the block
    // it resizes. Before this, `nominalHeight` (40) and `referenceWidth`
    // (41) scaled and `horizontalWidth`/`verticalHeight` did not, so a ×2
    // SCALE left AutoCAD's measurement of the ×1 block on a ×2 entity — a
    // stale pair with two readers (`wcsBoundsText.ts::mtextBoxSize`): the
    // SEMANTIC rectangle takes group 42 VERBATIM as its width, and the
    // EXACT select ring's right edge is `min(walk, group 42)`, so the ring
    // was truncated to HALF the painted block and a crossing window over
    // the visible suffix MISSED the entity — rounds 13/14/15's defect,
    // reached through the third writer.
    //
    // SCALED WHERE THAT IS EXACT, CLEARED WHERE IT IS NOT. The reference
    // position, checked rather than assumed:
    //
    //   * The Autodesk DXF reference marks both groups "read-only, ignored
    //     if supplied" (42: "horizontal width of the characters that make
    //     up the mtext entity. This value will always be equal to or less
    //     than the value of [group 41]"; 43: "vertical height of the mtext
    //     entity") — quoted verbatim in `ezdxf` 1.4.4's own
    //     `entities/mtext.py:151-156` beside `rect_width`/`rect_height`.
    //     AutoCAD does not store-and-carry them, it RECOMPUTES them from
    //     the laid-out block, so the only question here is which stored
    //     value equals what AutoCAD would recompute.
    //   * For a UNIFORM scale by `k` over a block that stays GEOMETRICALLY
    //     SIMILAR that value is `old × |k|`, by arithmetic: contents, style
    //     and inline codes untouched, `nominalHeight × |k|`, wrap column
    //     `referenceWidth × |k|` so every line breaks after the same glyph,
    //     every advance × `|k|`.
    //   * Similarity is a PRECONDITION, not a given —
    //     `drawing-model::mtextUniformScalePreservesLayoutShape` is the
    //     predicate and its header carries the counter-example: an ABSOLUTE
    //     inline `\H5;` run paints at a WORLD height this transform does
    //     not move (`mtextPaintedLayout`: `value / nominalHeight`), so its
    //     advances stay put while the rest of the block grows AND the
    //     wrap-column invariance above breaks with them. Scaling 42/43
    //     there would trade round-15's stale pair for a DIFFERENTLY stale
    //     one. On `false` this clears instead — round 14's action for round
    //     14's reason: the new measurement is unknowable here, and 0 is the
    //     "no group 42/43" state the writer omits and AutoCAD recomputes.
    //   * This repo's READ side already scales the pair in the similar
    //     case, in the same words: `wcsBoundsLeaf.ts::mtextLocalCorners`
    //     re-evaluates an annotative per-scale context as `horizontalWidth
    //     × scale, verticalHeight × scale` because "groups 42/43 are
    //     paper-space master fields and do scale uniformly". Write and read
    //     may not disagree (CLAUDE.md ПРИОРИТЕТ #2.1 — one truth).
    //   * NOT an `ezdxf` parity claim: its `MText.transform()` scales 40,
    //     41, the inline codes, the insertion, the direction and the column
    //     data and leaves `rect_width`/`rect_height` untouched, and
    //     `disassemble.py` then reads that stale pair back as the box —
    //     the same gap #804 reports. Its `MTextColumns.transform` DOES
    //     scale `total_width`/`total_height`/`heights`, so "a cached
    //     measured extent scales with the transform" is ezdxf's own
    //     position elsewhere; we are not copying its 42/43 behaviour.
    //   * Scaling also restores a DXF-documented INVARIANT this function
    //     was breaking: "42 will always be equal to or less than 41". A
    //     SHRINKING scale used to divide 41 and leave 42 alone, which can
    //     put 42 ABOVE 41; scaling both by one factor preserves it, and
    //     clearing 42 satisfies it trivially.
    //
    // PRESENCE, for free and not by a guard: `0` is the schema default and
    // the "no group 42/43" state the writer omits, and `0 × absFactor === 0`
    // — an MTEXT that never carried a measurement still does not. No
    // `presencePreservingValue` treatment is needed (unlike the Block
    // attribute heights below, whose implied default is 2.5), and the
    // predicate is skipped entirely for such an entity so the overwhelmingly
    // common case pays no probe.
    //
    // ONE UNIFORM FACTOR, because that is all this function receives: 42 is
    // a WIDTH and 43 a HEIGHT, so a NON-UNIFORM scale would owe them |sx|
    // and |sy| RESPECTIVELY, never one shared factor. `geometry/scale-
    // entities` declares a single `factor` (`packages/contracts/src/
    // editor.ts`), so no speculative second parameter is added for a caller
    // that does not exist. When a non-uniform scale lands, an axis-aligned
    // MTEXT takes |sx| on 42 and |sy| on 43, and one ROTATED off the scale
    // axes must CLEAR — a non-uniform scale SHEARS its glyph cells, the
    // block is not similar under ANY factor and NEITHER field has a correct
    // value.
    //
    // THE SWEEP behind that claim — every writer of an MTEXT's size outside
    // tests and fixtures, READ for this round rather than assumed:
    //   * THIS arm — the only one that RESIZES an existing MTEXT while
    //     carrying its 42/43 forward through a `...entity` spread. The
    //     defect, fixed above.
    //   * MOVE / STRETCH / MIRROR / ROTATE — `transformEntityCoords`'s
    //     `case "MText"` (insertion point + a genuinely custom group-11
    //     direction), `geometryReducerEditTrim.ts`'s STRETCH arm (insertion
    //     point), `mirrorTextReadable`/`mirrorTextFull` (insertion point,
    //     rotation, direction, generation flags) and `rotateEntityAngles`'s
    //     `case "MText"` (rotation). All SIZE-PRESERVING, so carrying
    //     42/43 forward there is CORRECT, not a gap — checked, and said out
    //     loud so the next reader does not have to check again.
    //   * CREATE — `geometryMutations.ts::createMtextEntity` and
    //     `geometry/create-mtext` build fresh entities with no 42/43.
    //   * EXPLODE, both twins — `blockMutations.ts`'s `case "MText"`
    //     (`SimpleMtextEntity`, the shell preview) and
    //     `apps/web::blockReducerHelpers.ts`'s (the document entity). Each
    //     builds a FRESH MTEXT whose object literal names no
    //     `horizontalWidth`/`verticalHeight` at all, so both already clear.
    //     KNOWN, pre-existing and OUT of this round's scope (#806): both
    //     scale the WIDTH-like `referenceWidth` and the HEIGHT-like
    //     `nominalHeight` by the SAME `Math.abs(scaleX)`, so a non-uniform
    //     INSERT explodes to the wrong text height — the same "one factor
    //     for a directional pair" shape this paragraph is about, one
    //     subsystem over.
    //   * `apps/web::scene3d/adapters/typed/transformBlockChild.ts`'s `case
    //     "MText"` spreads `...child` and scales `nominalHeight` by
    //     `|scale.y|` and `referenceWidth` by `|scale.x|` while carrying
    //     42/43 unscaled — the same shape, in a DERIVED render adapter
    //     (#808). It is LATENT, not live: nothing under `scene3d/` reads
    //     either group (grep, this round), and the block-child BOUNDS path
    //     does not go through it — `cad-core::blockDefinitionWalk` calls
    //     `mtextBounds` on the child in LOCAL block coordinates and
    //     transforms the resulting BOX, which scales 42/43 correctly by
    //     construction. Left alone deliberately: it is a derived
    //     representation with its own S10-XFORM parity budget and
    //     legacy-oracle fixtures, and it is the place the |sx| / |sy| split
    //     above would actually have to be made.
    //
    // INVERSE: `computeInverse` re-dispatches SCALE with `1 / factor`
    // (`geometryMutations.ts`), so 42/43 travel through exactly the same
    // `w × k × (1/k)` arithmetic as `nominalHeight` and `referenceWidth`
    // already do — never a separate fidelity. What that is worth was
    // MEASURED rather than asserted, in
    // `scaleEntityRadii.mtextMeasurement.property.test.ts`:
    //   * BIT-exact for a POWER-OF-TWO factor — the ×2 witness, ×0.5, ×4 —
    //     and only there. A merely dyadic `k = 75/1024` has no exactly
    //     representable `1 / k` and the suite's own shrunk counterexample
    //     brought `0.4375` back as `0.43750000000000006`, one ULP out; the
    //     claim is scoped to `2^n` because that is what held.
    //   * for EVERY other factor the pair's relative round-trip error is
    //     pinned `<=` the error `nominalHeight` takes on the same run, with
    //     the worst observed deviation asserted under 1e-14.
    // There is no presence ambiguity to snapshot (`0` maps to `0` both
    // ways), so unlike `overrideHeight`/`overrideRotation` below this pair
    // needs no `restore*` entry in the command.
    case "MText": {
      const scaledBase = {
        ...entity,
        nominalHeight: entity.nominalHeight * absFactor,
        referenceWidth: entity.referenceWidth * absFactor,
      };
      // targeted-cad-mutation-testing EQUIVALENT MUTANTS (round-16 verified
      // survivors) — 2 of the 8 Stryker tries on this guard:
      //   1. ConditionalExpression on the WHOLE test → `false` (the early-out
      //      never fires).
      //   2. BlockStatement — the `if` body emptied (the test still evaluates
      //      `true`, but nothing runs inside, so execution falls through).
      // Why equivalent for BOTH: this `if` is a PERFORMANCE short-circuit and
      // nothing else. For an entity whose pair is already `0 / 0` the code
      // below returns the same two values whichever way the predicate votes —
      // `0 × absFactor === 0` on the SIMILAR branch and a literal `0` on the
      // cleared one — so skipping the early-out changes no field, only the two
      // contents walks the predicate would run. Its value is that the
      // overwhelmingly common MTEXT (no measurement carried) never pays for
      // them; see `mtextScaleSimilarity.ts`'s COST section.
      // The OTHER 6 variants are real, killable bugs and are killed: the
      // `&&` → `||` swap and the three mutants of the FIRST operand fall to
      // this file's "THE WITNESS" case, and the two mutants of the SECOND
      // operand (`verticalHeight === 0` forced `true`, and `===` → `!==`) to
      // "a HALF-carried pair scales the half that IS there" — an MTEXT may
      // genuinely carry 43 without 42.
      if (entity.horizontalWidth === 0 && entity.verticalHeight === 0) {
        return scaledBase;
      }
      return mtextUniformScalePreservesLayoutShape(entity, absFactor)
        ? {
            ...scaledBase,
            horizontalWidth: entity.horizontalWidth * absFactor,
            verticalHeight: entity.verticalHeight * absFactor,
          }
        : { ...scaledBase, horizontalWidth: 0, verticalHeight: 0 };
    }
    case "Block":
      return {
        ...entity,
        scaleX: (entity.scaleX ?? 1) * factor,
        scaleY: (entity.scaleY ?? 1) * factor,
        // GitHub Codex review, PR #558 round 6 P2 — a WORLD-placed
        // attribute's text height must scale WITH its position (both
        // bypass the parent INSERT's own scaleX/scaleY at render/export —
        // `attdefFromEntity.ts`'s WORLD branch, `dxfBridge.attribExport
        // Position.ts`'s `overridePosition !== undefined` branch — so
        // bumping the Block's OWN scaleX/scaleY above, unlike a
        // block-local attribute, has ZERO effect on a WORLD attribute's
        // rendered/exported size). Matches standalone Text's `height *
        // absFactor` above — a WORLD attribute "rides exactly like a
        // standalone TEXT" (`mirrorRotationField.ts`'s header), so it
        // scales the same way. `presencePreservingValue` (implied default
        // 2.5, the WORLD-regime height fallback every render/export/bounds
        // site agrees on) keeps this byte-faithful: an attribute that
        // never carried an explicit `overrideHeight` and scales BACK to
        // exactly the implied default (only `factor === 1`, a degenerate
        // no-op scale) stays absent; every other case materialises the
        // scaled value explicitly, same rule as `overrideRotation`. A
        // block-local attribute (`overridePosition === undefined`) is left
        // untouched — same WORLD-vs-BLOCK-LOCAL gate as the sibling
        // `overridePosition` fix in `transformEntityCoords`'s Block case.
        // `?? []` — a regression-harness entity built from a minimal
        // literal (`tests/regression/autocad-parity/descriptors/command-
        // mutation/scale-block-insert.case.ts`, NOT schema-parsed, so
        // `blockInsertEntitySchema.attributes`'s `.default([])` never fills
        // in) can genuinely omit `attributes` — this crashed the L1
        // regression suite (`Cannot read properties of undefined (reading
        // 'map')`) before this guard; the two sibling Block-attribute call
        // sites in `geometryTransforms.ts` (rotateEntityAngles,
        // transformEntityCoords) had the identical latent gap, fixed
        // alongside this one for consistency.
        attributes: (entity.attributes ?? []).map((attr) => {
          if (attr.overridePosition === undefined) return attr;
          const height = presencePreservingValue(
            attr.overrideHeight,
            (attr.overrideHeight ?? 2.5) * absFactor,
            2.5,
          );
          // GitHub Codex review PR #558 round 2 (round-14 local read-only
          // follow-up) P2 — "Rotate WORLD attributes for negative scales".
          // `transformEntityCoords` already reflects a WORLD attribute's
          // `overridePosition` for a negative `factor` (the generic affine
          // `fn` doesn't distinguish sign), but its absolute `overrideRotation`
          // — which "rides exactly like a standalone TEXT" (this file's own
          // header) — went untouched, so the label stopped following the
          // scaled/reflected block. A single `factor` parameter always
          // negates the Block's OWN `scaleX` AND `scaleY` TOGETHER (two lines
          // above), which is mathematically a 180° ROTATION about the base
          // point (negating both axes = point reflection = 180° turn, not a
          // true single-axis mirror) — the SAME `scaleXNeg && scaleYNeg ⇒
          // +180°` rule `foldScalarRotationDeg` already applies to
          // block-local content. `presencePreservingRotation` (implied
          // default 0) keeps a SINGLE negative-factor scale byte-faithful:
          // an attribute that never carried an explicit `overrideRotation`
          // stays absent ONLY if this ONE call's +180° bump lands back on
          // exactly 0 (impossible for the ADDITIVE +180 rule on a genuinely-
          // absent 0 start — it always materialises to 180). Once
          // materialised, `presencePreservingValue`'s OWN documented rule
          // keeps it EXPLICIT going forward even if a LATER scale's result
          // happens to equal 0 again — this function alone is NOT a
          // presence-round-trip guarantee across MULTIPLE scales; undo
          // relies on the separate `restoreAttributeRotations` snapshot
          // (`geometryMutations.ts computeInverse`), same story as height's
          // own `restoreAttributeHeights`.
          //
          // targeted-cad-mutation-testing EQUIVALENT MUTANTS (round-14
          // verified survivors):
          //   1. EqualityOperator `factor < 0` → `factor <= 0`. Why
          //      equivalent: `geometry/scale-entities`' own command schema
          //      (`packages/contracts/src/editor.ts`) refines `factor` with
          //      `f !== 0 && Number.isFinite(f) && Math.abs(f) >
          //      FLOAT_EPSILON` — `factor === 0` is REJECTED before this
          //      code ever runs, so `< 0` and `<= 0` partition the SAME two
          //      non-empty regions for every value this function can
          //      actually receive.
          //   2. ArithmeticOperator `+ 180` → `- 180` (the line below). Why
          //      equivalent: `180 ≡ −180 (mod 360)` — for ANY `x`,
          //      `normalizeDeg360(x + 180)` and `normalizeDeg360(x − 180)`
          //      agree UP TO FLOATING-POINT PRECISION (verified by property
          //      test, `geometryTransforms.blockAttributeOverride.test.ts`'s
          //      "normalizeDeg360 — +180/-180 equivalence" suite, 2000 runs)
          //      — NOT bit-for-bit: `x+180` and `x−180` differ by exactly
          //      `360`, and IEEE754 `%` does not always round that shift
          //      back to the identical double (a genuine 1-ULP
          //      counter-example exists, e.g. `x ≈ 307.6395`). This matches
          //      the SAME tolerance level every other angle-degree
          //      comparison in this codebase already uses post-trig-
          //      transform (`toBeCloseTo`, never a raw `toBe` on a computed
          //      angle) — a 1-ULP (~1e-13 relative) difference has no
          //      observable effect through any consumer.
          const rotation =
            factor < 0
              ? presencePreservingRotation(
                  attr.overrideRotation,
                  normalizeDeg360((attr.overrideRotation ?? 0) + 180),
                )
              : attr.overrideRotation;
          if (height === undefined && rotation === attr.overrideRotation) {
            return attr;
          }
          const { overrideHeight: _h, overrideRotation: _r, ...rest } = attr;
          void _h;
          void _r;
          return {
            ...rest,
            ...(height !== undefined ? { overrideHeight: height } : {}),
            ...(rotation !== undefined ? { overrideRotation: rotation } : {}),
          };
        }),
      };
    case "Multileader":
      return {
        ...entity,
        textHeight: (entity.textHeight ?? 2.5) * absFactor,
      };
    // GitHub Codex review PR #785 round 1, F3 — a heavy POLYLINE's WIDTHS are
    // size-like and must scale WITH the path. `transformEntityCoords` scales
    // the vertex COORDINATES (the generic affine `fn`), but the widths live
    // here, so a 2x SCALE previously doubled the path and left the stroke at
    // its old width. The renderer reads exactly these four groups
    // (`scene3d/adapters/typed/polylineFromEntity.ts`: per-vertex 40/41
    // falling back to the entity-level 40/41) and the DXF writer emits them
    // verbatim, so the mismatch was visible AND exported.
    //
    // `absFactor`, not `factor`: a width is a magnitude — the SAME rule every
    // other case here uses (radius / height / patternScale), and the reason a
    // negative-factor SCALE (a 180° rotation about the base point) never
    // produces a negative width.
    //
    // PRESENCE BITS — the model distinguishes three states per field and the
    // scale must not collapse them (`polylineVertexRecordSchema`: "40 —
    // starting width; absent ⇒ inherit the polyline's default"):
    //   - an ABSENT per-vertex 40/41 means INHERIT the entity default, NOT
    //     "0". It stays absent and inherits the ALREADY-SCALED default below,
    //     which is exactly right — materialising `0 * absFactor` here would
    //     turn an inheriting vertex into a hard zero-width one.
    //   - an ABSENT entity-level 40/41 implies 0 (DXF group 40 default), and
    //     `0 * absFactor === 0`, so it stays absent: never fabricate a group
    //     the source never had (`presencePreservingValue`'s rule, whose
    //     implied-default-0 case this is).
    //   - an EXPLICIT `0` stays explicit (the conditional spread copies the
    //     scaled value back, keeping the key), so "width written as 0" never
    //     degrades into "width absent".
    // NOT scaled: `thickness` (group 39) — no `scaleEntityRadii` case scales
    // an entity's thickness today (Circle/Arc scale only `radius`), so a
    // Polyline exception would be a NEW, unreviewed convention; and
    // `elevation`, whose z is out of reach of the 2D planar edit contract.
    case "Polyline":
      return {
        ...entity,
        ...(entity.defaultStartWidth !== undefined
          ? { defaultStartWidth: entity.defaultStartWidth * absFactor }
          : {}),
        ...(entity.defaultEndWidth !== undefined
          ? { defaultEndWidth: entity.defaultEndWidth * absFactor }
          : {}),
        vertices: entity.vertices.map((v) => ({
          ...v,
          ...(v.startWidth !== undefined
            ? { startWidth: v.startWidth * absFactor }
            : {}),
          ...(v.endWidth !== undefined
            ? { endWidth: v.endWidth * absFactor }
            : {}),
        })),
      };
    case "Viewport":
      return {
        ...entity,
        width: entity.width * absFactor,
        height: entity.height * absFactor,
      };
    case "Hatch":
      return {
        ...entity,
        patternScale: (entity.patternScale ?? 1) * absFactor,
        boundaryPaths: entity.boundaryPaths.map((path) => ({
          ...path,
          edges: path.edges.map((edge) => {
            if (edge.edgeType === "arc") {
              return { ...edge, radius: edge.radius * absFactor };
            }
            // targeted-cad-mutation-testing EQUIVALENT MUTANTS (GitHub Codex
            // review PR #558 round 6, verified survivors — 2 of the 4
            // mutator variants Stryker tried on this guard):
            //   1. ConditionalExpression forced to `false` (this branch
            //      never fires, even for a genuine ellipticArc edge).
            //   2. StringLiteral `"ellipticArc"` → `""` (same effect on a
            //      genuine ellipticArc edge: `"ellipticArc" === ""` is
            //      `false`, so the branch never fires for it either).
            //   3. BlockStatement — the `if` body emptied (branch condition
            //      still evaluates `true` for a genuine ellipticArc edge,
            //      but nothing runs inside it, so execution falls through
            //      to the final `return edge` exactly as if the branch had
            //      not fired at all).
            // Why equivalent for ALL THREE: the branch's own body
            // (`{ ...edge, minorRatio: edge.minorRatio }`) re-spreads the
            // SAME field with its OWN unchanged value — `minorRatio` is a
            // dimensionless ratio, deliberately NOT scaled (see the comment
            // below). Skipping the branch entirely falls through to the
            // final `return edge` — the UNMODIFIED source object — which
            // carries the exact same `minorRatio` value already, since nothing
            // upstream mutated `edge` yet. A no-op copy and a skip-to-
            // unchanged-original are value-identical for every field this
            // function's callers observe (Vitest's `toStrictEqual`
            // included — only the object REFERENCE differs, never
            // asserted). The OTHER 2 variants of this same mutated guard
            // (`ConditionalExpression → true`, `EqualityOperator → !==`)
            // ARE real, killable bugs — see
            // `scaleEntityRadii.test.ts`'s "'line' boundary edge is
            // returned STRICTLY unchanged" case: forcing this branch to
            // fire for a NON-ellipticArc edge (a "line" edge has no
            // `minorRatio` field at all) adds a spurious `minorRatio:
            // undefined` key, which IS observable.
            if (edge.edgeType === "ellipticArc") {
              return { ...edge, minorRatio: edge.minorRatio }; // ratio is scale-invariant
            }
            return edge;
          }),
        })),
      };
    default:
      return entity;
  }
}
